import React, { memo } from "react";
import { useDispatch } from "react-redux";
import {
  CardPromptBinType,
  CardPromptType,
  PromptType,
  RangeType,
} from "../../../store/cardPrompt/types";
import {
  addCardBin,
  addPromptEntry,
  addRangeEntry,
  deleteCardBin,
  removePromptEntry,
  removeRangeEntry,
} from "../../../store/cardPrompt/cardPromptSlice";
import List from "../../../common/List/List";
import OrderedList from "../../../common/OrderedList";
import Prompt from "./Prompt";
import { FormikProps } from "formik";
import BinRange from "./BinRange";
import produce from "immer";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import { Box } from "@mui/material";

interface CardPromptBins {
  cardPromptData: CardPromptType;
  formikProps: FormikProps<CardPromptType>;
}

const CardPromptBins = ({
  cardPromptData: selected,
  formikProps,
}: CardPromptBins) => {
  const dispatch = useDispatch();

  const onAddCardBin = (formikProps: FormikProps<CardPromptType>) => {
    const newEntry = {
      Range: [],
      Prompt: [],
    };
    const binEntries = produce(
      formikProps.values.attributes.Bin ?? [],
      (draft) => {
        draft.push(newEntry);
      }
    );
    formikProps.setFieldValue(`attributes.Bin`, binEntries);
    dispatch(addCardBin(newEntry));
  };

  const onRemoveCardBin = (
    formikProps: FormikProps<CardPromptType>,
    item: CardPromptBinType,
    index: number
  ) => {
    const binEntries = produce(
      formikProps.values.attributes.Bin ?? [],
      (draft) => {
        draft.splice(index, 1);
      }
    );
    dispatch(deleteCardBin({ index }));
  };

  const addPrompt = (
    formikProps: FormikProps<CardPromptType>,
    cardBinIndex: number
  ) => {
    const newEntry = {
      Label: "",
      Hint: "",
      Type: "TEXT",
      MinCharacters: undefined,
      MaxCharacters: undefined,
      Options: [],
      Tag: "",
      Language: "en",
      Condition: "",
    };
    const prompts = produce(
      formikProps.values.attributes.Bin[cardBinIndex].Prompt ?? [],
      (draft) => {
        draft.push(newEntry);
      }
    );
    formikProps.setFieldValue(
      `attributes.Bin[${cardBinIndex}].Prompt`,
      prompts
    );
    dispatch(
      addPromptEntry({
        cardBinIndex: cardBinIndex,
        newEntry: newEntry,
      })
    );
  };

  const removePrompt = (
    formikProps: FormikProps<CardPromptType>,
    cardBinIndex: number,
    promptIndex: number
  ) => {
    const prompts = produce(
      formikProps.values.attributes.Bin[cardBinIndex].Prompt,
      (draft: PromptType[]) => {
        if (draft.length > 0) {
          draft.splice(promptIndex, 1);
        }
      }
    );
    formikProps.setFieldValue(
      `attributes.Bin[${cardBinIndex}].Prompt`,
      prompts
    );
    dispatch(
      removePromptEntry({
        cardBinIndex: cardBinIndex,
        promptIndex: promptIndex,
      })
    );
  };

  const addRange = (
    formikProps: FormikProps<CardPromptType>,
    cardBinIndex: number
  ) => {
    const newEntry = {
      From: "",
      To: "",
      Length: 16,
      Account: [],
    };
    const range = produce(
      formikProps.values.attributes.Bin[cardBinIndex].Range ?? [],
      (draft) => {
        draft.push(newEntry);
      }
    );
    formikProps.setFieldValue(`attributes.Bin[${cardBinIndex}].Range`, range);
    dispatch(
      addRangeEntry({
        cardBinIndex,
        newEntry,
      })
    );
  };

  const removeRange = (
    formikProps: FormikProps<CardPromptType>,
    cardBinIndex: number,
    rangeIndex: number
  ) => {
    const ranges = produce(
      formikProps.values.attributes.Bin[cardBinIndex].Range ?? [],
      (draft: RangeType[]) => {
        if (draft.length > 0) {
          draft.splice(rangeIndex, 1);
        }
      }
    );
    formikProps.setFieldValue(`attributes.Bin[${cardBinIndex}].Range`, ranges);
    dispatch(
      removeRangeEntry({
        cardBinIndex: cardBinIndex,
        rangeIndex: rangeIndex,
      })
    );
  };

  return (
    <>
      <List
        heading="Card Bins"
        addButtonLabel="Add Bin"
        items={selected ? selected.attributes.Bin : []}
        onAddItem={() => {
          onAddCardBin(formikProps);
        }}
        onRemoveItem={(item, index) => {
          onRemoveCardBin(formikProps, item, index);
        }}
        render={(item, cardBinIndex) => (
          <Box width={1}>
            <SectionWrapper isChild isWhiteBG title="Bin Range">
              <OrderedList
                items={item.Range}
                itemComponent={(rangeEntry, rangeIndex) => {
                  const namespace = `attributes.Bin[${cardBinIndex}].Range[${rangeIndex}]`;
                  return (
                    <BinRange
                      binRange={rangeEntry}
                      namespace={namespace}
                      formikProps={formikProps}
                    />
                  );
                }}
                addButtonLabel="Add BIN range"
                cellStyle={{
                  padding: 8,
                  border: "1px solid #ddd",
                  borderRadius: 5,
                  marginBottom: "1rem",
                }}
                onAdd={() => {
                  addRange(formikProps, cardBinIndex);
                }}
                onDelete={(rangeIndex) => {
                  removeRange(formikProps, cardBinIndex, rangeIndex);
                }}
              />
            </SectionWrapper>

            <SectionWrapper isChild isWhiteBG title="Prompt">
              <OrderedList
                items={item.Prompt}
                itemComponent={(promptEntry, promptIndex) => {
                  const namespace = `attributes.Bin[${cardBinIndex}].Prompt[${promptIndex}]`;
                  return (
                    <Prompt
                      namespace={namespace}
                      prompt={promptEntry}
                      cardBinIndex={cardBinIndex}
                      promptIndex={promptIndex}
                    />
                  );
                }}
                cellStyle={{
                  padding: 8,
                  border: "1px solid #ddd",
                  borderRadius: 5,
                  marginBottom: "1rem",
                }}
                addButtonLabel="Add prompt entry"
                onAdd={() => {
                  addPrompt(formikProps, cardBinIndex);
                }}
                onDelete={(promptIndex) => {
                  removePrompt(formikProps, cardBinIndex, promptIndex);
                }}
              />
            </SectionWrapper>
          </Box>
        )}
      />
    </>
  );
};

export default memo(CardPromptBins);
